// src/services/taskSync.service.js
import taskService from './task.service';
import teamTasksService from './teamTasks.service';

class TaskSyncService {
    /**
     * Sync personal tasks with their corresponding team tasks
     * This function should be called when a team task is updated
     */
    async syncPersonalTasksFromTeamTask(teamTaskId) {
        try {
            console.log(`🔄 Syncing personal tasks for team task ${teamTaskId}`);

            // Get the updated team task
            const teamTaskResponse = await teamTasksService.getTeamTask(teamTaskId);
            if (!teamTaskResponse.success) {
                console.error('Failed to get team task for sync');
                return;
            }

            const teamTask = teamTaskResponse.data;

            // Find all personal tasks that reference this team task
            const allTasksResponse = await taskService.getTasks({
                category: 'team_task'
            });

            if (!allTasksResponse.success) {
                console.error('Failed to get personal tasks for sync');
                return;
            }

            const personalTasks = allTasksResponse.data.filter(task =>
                task.team_task_id === teamTaskId.toString()
            );

            console.log(`Found ${personalTasks.length} personal tasks to sync`);

            // Update each personal task to match the team task
            const syncPromises = personalTasks.map(async (personalTask) => {
                const updatedData = {
                    title: teamTask.title,
                    description: teamTask.description || personalTask.description,
                    priority: teamTask.priority || personalTask.priority,
                    deadline: teamTask.deadline,
                    estimated_hours: teamTask.estimated_hours,
                    // Keep personal task specific fields
                    status: personalTask.status, // Don't override personal task status
                    team_task_original_status: teamTask.status
                };

                const updateResponse = await taskService.updateTask(personalTask.id, updatedData);
                if (updateResponse.success) {
                    console.log(`✅ Synced personal task ${personalTask.id} with team task changes`);
                }
                return updateResponse;
            });

            const results = await Promise.allSettled(syncPromises);
            const successful = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.filter(r => r.status === 'rejected').length;

            console.log(`📊 Sync completed: ${successful} successful, ${failed} failed`);

            return { success: true, synced: successful, failed };

        } catch (error) {
            console.error('Error syncing personal tasks from team task:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Sync team task status when a personal task is completed
     * This can be used to optionally update team task status when all assignees complete their personal tasks
     */
    async syncTeamTaskFromPersonalTask(personalTaskId, newStatus) {
        try {
            // Get the personal task
            const personalTaskResponse = await taskService.getTask(personalTaskId);
            if (!personalTaskResponse.success) {
                console.error('Failed to get personal task for sync');
                return;
            }

            const personalTask = personalTaskResponse.data;

            if (!personalTask.team_task_id) {
                console.log('Personal task is not linked to a team task, skipping sync');
                return { success: true, skipped: true };
            }

            console.log(`🔄 Syncing team task ${personalTask.team_task_id} from personal task ${personalTaskId}`);

            // Get all personal tasks linked to this team task
            const allTasksResponse = await taskService.getTasks({
                category: 'team_task'
            });

            if (!allTasksResponse.success) {
                console.error('Failed to get personal tasks for sync');
                return;
            }

            const linkedPersonalTasks = allTasksResponse.data.filter(task =>
                task.team_task_id === personalTask.team_task_id
            );

            // Check if all personal tasks are completed
            const allCompleted = linkedPersonalTasks.every(task => task.status === 'completed');

            if (allCompleted && newStatus === 'completed') {
                // Optionally update team task status to completed
                const updateResponse = await teamTasksService.updateTeamTask(
                    personalTask.team_task_id,
                    { status: 'completed' }
                );

                if (updateResponse.success) {
                    console.log(`✅ Updated team task ${personalTask.team_task_id} to completed`);
                }

                return updateResponse;
            } else {
                console.log(`📊 Not all personal tasks are completed, keeping team task status`);
                return { success: true, skipped: true, reason: 'not_all_completed' };
            }

        } catch (error) {
            console.error('Error syncing team task from personal task:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Check for orphaned personal tasks (team task no longer exists)
     * and optionally clean them up
     */
    async cleanupOrphanedPersonalTasks() {
        try {
            console.log('🧹 Checking for orphaned personal tasks...');

            // Get all personal tasks that are linked to team tasks
            const allTasksResponse = await taskService.getTasks({
                category: 'team_task'
            });

            if (!allTasksResponse.success) {
                console.error('Failed to get personal tasks for cleanup');
                return;
            }

            const linkedPersonalTasks = allTasksResponse.data.filter(task => task.team_task_id);

            const orphanedTasks = [];

            // Check each linked personal task
            for (const personalTask of linkedPersonalTasks) {
                try {
                    const teamTaskResponse = await teamTasksService.getTeamTask(personalTask.team_task_id);
                    if (!teamTaskResponse.success) {
                        // Team task doesn't exist, this is an orphaned personal task
                        orphanedTasks.push(personalTask);
                    }
                } catch (error) {
                    // Team task doesn't exist, this is an orphaned personal task
                    orphanedTasks.push(personalTask);
                }
            }

            console.log(`Found ${orphanedTasks.length} orphaned personal tasks`);

            // Optionally clean up orphaned tasks
            // For now, just log them. In production, you might want to:
            // - Delete them
            // - Mark them as orphaned
            // - Notify the user

            orphanedTasks.forEach(task => {
                console.log(`🚨 Orphaned personal task: ${task.id} - ${task.title} (linked to team task ${task.team_task_id})`);
            });

            return { success: true, orphanedCount: orphanedTasks.length, orphanedTasks };

        } catch (error) {
            console.error('Error cleaning up orphaned personal tasks:', error);
            return { success: false, error: error.message };
        }
    }
}

export default new TaskSyncService();